/**
 * @description 관제 ↔ 구조자 폰 사이의 기기 제어 규약
 *
 * 기존 'Video' / 'Audio' 메시지는 토글 방식이라 관제 화면과 폰의 실제 상태가
 * 어긋나면 되돌릴 방법이 없었다. 여기서는 명령을 멱등(set)으로 바꾸고,
 * 폰이 명령을 적용한 뒤 실제 상태를 되돌려 보내 관제가 그 값만 보고 그리도록 한다.
 */

export const CONTROL_TYPE = 'Control';

/** 관제 → 폰 명령 */
export type ControlCommand =
  | 'video' // 카메라 ON/OFF
  | 'audio' // 마이크 ON/OFF
  | 'torch' // 플래시 ON/OFF
  | 'camera' // 전/후면 전환
  | 'zoom' // 줌 인/아웃
  | 'caps'; // 현재 상태/지원 기능 재요청

/** 폰 → 관제 보고 */
export const STATE_REPORT = 'state';

export type CameraFacing = 'front' | 'rear';
export type ZoomDirection = 'in' | 'out';
export type ControlValue = boolean | CameraFacing | ZoomDirection | null;

/** 폰이 실제로 지원하는 기능. 기기·카메라마다 다르므로 폰이 런타임에 확인해 보고한다. */
export interface DeviceCaps {
  torch: boolean;
  camera: boolean;
  zoom: { min: number; max: number } | null;
}

/** 폰의 현재 상태 */
export interface DeviceState {
  video: boolean;
  audio: boolean;
  torch: boolean;
  facing: CameraFacing;
  zoom: number | null;
  /** 서버 녹화가 진행 중인지 */
  recording: boolean;
  /** 녹화 시작/중지가 실패한 경우의 사유. 성공하면 null. */
  recordingError: string | null;
  cameraError: string | null;
}

export interface DeviceReport {
  phone: string;
  caps: DeviceCaps;
  state: DeviceState;
}

export interface ControlMessage {
  type: typeof CONTROL_TYPE;
  cmd: ControlCommand | typeof STATE_REPORT;
  value?: ControlValue;
  phone?: string;
  caps?: DeviceCaps;
  state?: DeviceState;
  roomId?: string;
}

/** 아직 보고를 받지 못한 폰은 모든 부가 기능을 미지원으로 본다. */
export const EMPTY_CAPS: DeviceCaps = { torch: false, camera: false, zoom: null };

/** 요구사항상 카메라/마이크/플래시는 모두 OFF 로 시작한다. */
export const DEFAULT_STATE: DeviceState = {
  video: false,
  audio: false,
  torch: false,
  facing: 'rear',
  zoom: null,
  recording: false,
  recordingError: null,
  cameraError: null,
};

export const encodeMessage = (message: unknown): string => encodeURIComponent(JSON.stringify(message));

export const decodeMessage = (text: string): any => JSON.parse(decodeURIComponent(text));

export const isControlMessage = (received: any): boolean =>
  received !== null && typeof received === 'object' && received.type === CONTROL_TYPE;

/** 관제 → 폰 명령 생성 */
export const buildCommand = (cmd: ControlCommand, value: ControlValue, roomId?: string): ControlMessage => ({
  type: CONTROL_TYPE,
  cmd,
  value,
  roomId,
});

/** 폰 → 관제 상태 보고 생성 */
export const buildStateReport = (
  phone: string,
  caps: DeviceCaps,
  state: DeviceState,
  roomId?: string
): ControlMessage => ({
  type: CONTROL_TYPE,
  cmd: STATE_REPORT,
  phone,
  caps,
  state,
  roomId,
});

export type ControlButton = 'video' | 'audio' | 'capture' | 'torch' | 'camera' | 'zoomIn' | 'zoomOut';

/**
 * 관제가 보고 있는 수신 스트림의 상태.
 *
 * WebRTC 세션이 맺어진 것(connected)과 실제로 영상 프레임이 그려지고 있는 것(hasFrame)은
 * 다른 시점이다. 연결 직후 첫 프레임 전이나 상대가 카메라를 끈 뒤에는 세션은 살아 있어도
 * 캡쳐할 그림이 없다.
 */
export interface StreamState {
  /** PLAYING 상태 — WebRTC 세션 연결 완료 */
  connected: boolean;
  /** <video> 요소에 디코딩된 프레임이 존재 */
  hasFrame: boolean;
  /** 원격(폰) 카메라가 켜져 있음 */
  videoOn: boolean;
}

/**
 * 관제 버튼의 활성 여부를 판정하는 단일 지점.
 *
 * 정책: 카메라가 꺼져 있으면 플래시·전환·줌은 모두 비활성이다.
 * 카메라를 끄면 폰이 플래시도 함께 끄므로, 관제에도 같은 규칙을 적용해야 화면과 실제가 맞는다.
 * 캡쳐는 영상을 저장하는 기능이므로 세션 연결만으로는 부족하고 실제 프레임이 있어야 한다.
 */
export const isControlEnabled = (
  button: ControlButton,
  caps: DeviceCaps | undefined,
  state: DeviceState | undefined,
  stream: StreamState
): boolean => {
  switch (button) {
    // 구버전 폰(상태 보고를 하지 않는)에서도 계속 쓸 수 있어야 한다
    case 'video':
    case 'audio':
      return true;
    case 'capture':
      return stream.connected && stream.hasFrame && stream.videoOn;
    case 'torch':
      return !!caps?.torch && !!state?.video;
    case 'camera':
      return !!caps?.camera && !!state?.video;
    case 'zoomIn':
      if (!caps?.zoom || !state?.video) return false;
      return state.zoom === null || state.zoom < caps.zoom.max;
    case 'zoomOut':
      if (!caps?.zoom || !state?.video) return false;
      return state.zoom === null || state.zoom > caps.zoom.min;
    default:
      return false;
  }
};
